
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Loader } from '@googlemaps/js-api-loader';
import { GeocodeResult, LatLngLiteral } from '@googlemaps/google-maps-services-js';
import { debounce } from 'lodash';

import { sharedState } from '../../../framework/state';
import {
    getGoogleMapsAutocomplete,
    getGoogleMapsGeoCoding,
    getGoogleMapsJavascriptApiKey,
} from '../../client/google_maps_client';

@Component
export default class GoogleMaps extends Vue {
    @Prop(Number) readonly height!: number;
    @Prop(Boolean) readonly input!: boolean;
    @Prop(Boolean) readonly disabled!: boolean;
    @Prop(String) readonly address!: string;
    @Prop(Object) readonly coords!: LatLngLiteral;

    // State
    shared = sharedState;
    local = {
        address: '',
        isFetching: false,
        map: {} as google.maps.Map,
        addressOptions: [] as string[],
        selected: {} as any,
        geoCodeResult: [] as GeocodeResult[],
        locationMarker: undefined as google.maps.Marker | undefined,
        mapLoading: false,
        apiKey: '',
        isMapLoaded: false,
    };

    // Functions
    async mounted() {
        this.local.apiKey = (await getGoogleMapsJavascriptApiKey()).apiKey;
        // Wait for the next tick to ensure DOM is rendered
        await this.$nextTick();
        this.initMap();
    }

    initMap(): void {
        const mapElement = document.getElementById('map');
        if (!mapElement) {
            console.error('Map element not found');
            return;
        }

        if (!this.local.apiKey) {
            console.error('API key not available');
            return;
        }

        this.loader()
            .importLibrary('maps')
            .then(() => {
                this.local.isMapLoaded = true;
                this.local.map = new google.maps.Map(mapElement, {
                    center: { lat: 60.293761, lng: 24.9657867 },
                    zoom: 10,
                });

                if (this.coords) {
                    this.setMarker(this.coords);
                    this.centerMap(this.coords);
                }
            })
            .catch((error: any) => {
                console.error(`Error loading Google Maps: ${error.toString()}`);
            });
    }

    get mapHeight(): string {
        return `height: ${this.height}px;`;
    }

    @Watch('address')
    inputAddressChanged(newAddress: string): void {
        if (newAddress.length === 0) {
            return;
        }
        this.local.address = newAddress;
        this.local.selected = newAddress;
    }

    @Watch('coords')
    inputCoordsChanged(coords: LatLngLiteral): void {
        if (this.local.isMapLoaded) {
            this.setMarker(coords);
            this.centerMap(coords);
        }
    }

    @Watch('local.address')
    async addressChanged(newAddress: string) {
        if (newAddress.length === 0) {
            this.local.addressOptions = [];
            return;
        }

        this.local.isFetching = true;
        await debounce(this.getAutocompleteResults, 100)();
        this.local.isFetching = false;
    }

    @Watch('local.selected')
    async geocodeSelectedAddress(address: string) {
        if (address) {
            const geoCodeResults = await this.getGeocodingResult();
            const coords = {
                lat: geoCodeResults[0].geometry.location.lat,
                lng: geoCodeResults[0].geometry.location.lng,
            };

            if (this.local.isMapLoaded) {
                this.setMarker(coords);
                this.centerMap(coords);
            }
            this.emitAddress(geoCodeResults[0]);
        }
    }

    private async getAutocompleteResults() {
        this.local.addressOptions = await getGoogleMapsAutocomplete(this.local.address, this.$i18n.locale);
    }

    private async getGeocodingResult() {
        return await getGoogleMapsGeoCoding(this.local.selected, this.$i18n.locale);
    }

    private loader(): Loader {
        return new Loader({
            apiKey: this.local.apiKey,
            version: 'weekly',
        });
    }

    private emitAddress(geoCodeResult: GeocodeResult): void {
        this.$emit('geocoded', geoCodeResult);
    }

    private setMarker(coords: LatLngLiteral): void {
        if (!this.local.isMapLoaded || typeof google === 'undefined') {
            return;
        }

        if (this.local.locationMarker) {
            this.local.locationMarker.setMap(null);
        }

        this.local.locationMarker = new google.maps.Marker({
            animation: google.maps.Animation.DROP,
            map: this.local.map,
            position: {
                lat: coords.lat,
                lng: coords.lng,
            },
        });
    }

    private centerMap(coords: LatLngLiteral): void {
        if (!this.local.isMapLoaded || typeof google === 'undefined') {
            return;
        }

        this.local.map.setCenter({
            lat: coords.lat,
            lng: coords.lng,
        });

        this.local.map.setZoom(14);
    }
}
